import { lazy, Suspense } from "react";
import { useAuthUser } from "@/context/auth/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router";
import { useSidebar } from "@/components/ui/sidebar.utils";
import { usePlanesOfrecibles } from "@/hooks/react-query/Billing/usePlanChange";
import {
  encontrarPlanActual,
  esElPlanMasCaro,
} from "@/services/subscription/planActual";
import { formatDecimalString } from "@/utils/formatCurrency";
import { useTheme } from "next-themes";

// Iridescence usa `ogl`. Hasta acá era un import eager en la sidebar → ogl
// (60KB+) se cargaba en TODA página autenticada aunque el usuario nunca
// expandiera la sidebar. Lazy-loaded, ogl solo viaja cuando este card se
// renderiza y solo lo paga el owner que ve este efecto decorativo.
const Iridescence = lazy(() => import("@/components/Iridescence"));

/**
 * Un solo ícono para todos los planes.
 *
 * Antes se elegía por `tier` (starter/professional/enterprise), y **el contrato
 * nuevo no tiene `tier`**: la allowlist del borde no lo incluye. Inventarlo desde
 * el precio o el nombre sería adivinar una categoría comercial.
 */
const planIcon = <CreditCard className="h-5 w-5" />;

// Color único para todos los planes (mismo que el banner de home)
const SUBSCRIPTION_CARD_COLOR: [number, number, number] = [1, 0.2, 0.4];

export function SubscriptionCard() {
  const user = useAuthUser();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const { theme } = useTheme();
  // Sólo el dueño: el carril nuevo pide token de negocio con `role = owner` y a
  // un empleado le contesta 403. Gatear el hook evita pedir algo que ya sabemos
  // que va a fallar — y la tarjeta igual no se le muestra.
  const esDueno = user?.type === "owner";
  const {
    planes,
    isLoading: cargandoPlanes,
    isError: fallaronLosPlanes,
  } = usePlanesOfrecibles(esDueno);

  // Solo mostrar para owners
  if (!esDueno) {
    return null;
  }

  // El plan actual se deduce del entitlement (`modules`) mientras el borde no
  // lo exponga (trd-bff#272). La regla —incluidas las dos trampas que tenía la
  // versión anterior— vive en `encontrarPlanActual`, que se prueba aparte.
  const currentPlan = encontrarPlanActual(planes, user?.modules);

  /**
   * Un catálogo VACÍO no es un fallo: quiere decir que la org no publicó ningún
   * plan ofrecible. El borde ya filtra por `status: active` Y `is_public`,
   * fail-closed, así que `{"plans": []}` es una respuesta válida. Mostrarlo como
   * error diría que algo se rompió cuando no se rompió nada.
   */
  const sinPlanesPublicados =
    !cargandoPlanes && !fallaronLosPlanes && planes.length === 0;

  /**
   * Si NO sabemos el plan, la tarjeta no lo inventa.
   *
   * Antes, cualquier fallo del catálogo dejaba `currentPlan` en `undefined` y la
   * tarjeta mostraba "Plan Gratuito · Gratis": una afirmación sobre la plata del
   * usuario construida sobre un error de red. Un dueño que paga leía que no
   * paga.
   */
  const sabemosElPlan =
    !cargandoPlanes && !fallaronLosPlanes && currentPlan !== null;
  const esElTope = esElPlanMasCaro(planes, currentPlan?.plan_id);

  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  // Sidebar colapsado - mostrar solo icono
  if (state === "collapsed") {
    return (
      <div className="flex items-center justify-center pb-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/mi-suscripcion")}
          className="h-9 w-9"
        >
          {planIcon}
        </Button>
      </div>
    );
  }

  // Sidebar expandido - mostrar card completa con diseño moderno
  return (
    <div
      className={`relative mx-2 mb-2 ${
        isDark
          ? "p-[2px] rounded-lg bg-gradient-to-br from-primary/20 to-purple-500/20"
          : ""
      }`}
    >
      <Card className="overflow-hidden relative border-none rounded-lg cursor-pointer group">
        {/* Fondo animado. Suspense con fallback null porque es puramente
            decorativo — mientras `ogl` baja, el card sigue clickeable sin
            fondo en vez de mostrar un loader. */}
        <div className="absolute inset-0 w-screen h-screen">
          <Suspense fallback={null}>
            <Iridescence
              color={SUBSCRIPTION_CARD_COLOR}
              mouseReact={false}
              amplitude={0.1}
              speed={0.2}
            />
          </Suspense>
        </div>

        {/* Overlay para mejor legibilidad */}
        <div className="absolute inset-0 bg-black/20" />

        {/* Contenido */}
        <div
          className="p-4 relative z-10"
          onClick={() => navigate("/mi-suscripcion")}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                {planIcon}
              </div>
              <div>
                <p className="text-xs text-white/70 font-medium">
                  Tu plan actual
                </p>
                <p className="text-sm font-bold text-white">
                  {cargandoPlanes
                    ? "Cargando…"
                    : sabemosElPlan
                    ? currentPlan?.name || currentPlan?.plan_id
                    : sinPlanesPublicados
                    ? "Sin planes publicados"
                    : "No pudimos cargarlo"}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-3">
            {/* Sin plan conocido NO se imprime un precio: "Gratis" sobre un
                fallo de red es peor que un guión. */}
            {/* La plata llega como STRING decimal ("40000.00"): va por
                `formatDecimalString`, nunca por un parser de float. Y
                `price: null` con `is_free` es un plan gratis, no un precio que
                falta. */}
            <p className="text-lg font-bold text-white">
              {!sabemosElPlan ? (
                <span className="text-white/70">—</span>
              ) : currentPlan?.is_free || !currentPlan?.price ? (
                "Gratis"
              ) : (
                <>
                  {formatDecimalString(currentPlan.price)}
                  {currentPlan.interval_days ? (
                    <span className="text-xs font-normal text-white/70">
                      {" "}
                      cada {currentPlan.interval_days} días
                    </span>
                  ) : null}
                </>
              )}
            </p>
          </div>

          <Button
            size="sm"
            className="w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-white/20 h-8 text-xs font-medium group-hover:bg-white/40 transition-all"
            variant="outline"
          >
            {!sabemosElPlan
              ? "Ver mi suscripción"
              : esElTope
              ? "Ver plan"
              : "Mejorar plan"}{" "}
            <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
